import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';

import { AuthenticationService } from '../services/authentication.service';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { User } from 'app/interfaces/user';

@Injectable({
	providedIn: 'root',
})
export class AuthGuard implements CanActivate {

	private serverConfigParsed: BehaviorSubject<boolean>;

	constructor(
		private authenticationService: AuthenticationService,
		private router: Router) {

		this.serverConfigParsed = new BehaviorSubject<boolean>(false);
	}

	reset() {
		this.authenticationService.setAuthenticated(false);
	}

	canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
		const url: string = state.url;
		if (url.startsWith('/login/reset-password')) {
			return of(true);
		} else if (this.authenticationService.isAuthenticated()) {
			if (this.authenticationService.hasPublicationContext()) {
				if (url === '/login') {
					this.authenticationService.goToLocation();
				}
				return of(true);
			} else {
				if (url.startsWith('/login')) {
					return of(true);
				}
				this.saveLastVisitedUrl(url);
				return of(false);
			}
		} else if (!this.authenticationService.isAuthenticated() && !this.authenticationService.isLoggedOut()) {
			this.saveLastVisitedUrl(url);

			return this.checkLogin(url).pipe(
				map((success: boolean) => {
					if (this.authenticationService.isAuthenticated() && this.authenticationService.hasPublicationContext()) {
						this.authenticationService.goToLocation();
					} else {
						if (url.startsWith('/login')) {
							return true;
						}
						return success;
					}
				}
				));
		} else if (url.startsWith('/login')) {
			return of(true);
		}
		return this.checkLogin(url);
	}

	canActivateChild(route: ActivatedRouteSnapshot,	state: RouterStateSnapshot): Observable<boolean> {
		return this.canActivate(route, state);
	}

	saveLastVisitedUrl(url: string) {
		if (!url.startsWith('/login')) {
			this.authenticationService.lastVisitedUrl = url;
		}
	}

	redirectToLoginScreen(url: string): Observable<boolean> {
		const subject = this.serverConfigParsed;
		subject.subscribe(() => {
			if (!url.startsWith('/login')) {
				this.saveLastVisitedUrl(url);
				this.router.navigate(['/login']);
				return true;
			}
			return false;
		});
		return subject;
	}

	checkLogin(url: string): Observable<boolean> {
		if (this.authenticationService.isLoggedOut() && !url.startsWith('/login')) {
			this.saveLastVisitedUrl(url);
			this.reset();
		}
		return this.authenticationService.checkSession()
			.pipe(
				mergeMap((result: any) => this.authenticationService.getUser(true)
					.pipe(
						map((user: User) => {
							this.authenticationService.setAuthenticated(true);
							this.authenticationService.checkForPublication(false);
							if (!user.currentContext) {
								return false;
							} else {
								return this.authenticationService.isAuthenticated();
							}
						})
					)),
				catchError((error: any) => {
					this.reset();
					return this.redirectToLoginScreen(url);
				})
			);
	}
}
